/**
 * Camila Analytics — admin-only conversational analyst for the Camila
 * WhatsApp bot. Left column shows aggregated KPIs, funnel, abandonment
 * distribution. Right column is a chat with an AI analyst that reads
 * Camila's real conversations and answers questions like
 * "por qué los clientes abandonan", "qué patrones hay con los que preguntan precio", etc.
 */
import React, { useEffect, useState, useRef, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, Send, RefreshCw, AlertTriangle, TrendingDown, MessageSquare, Link as LinkIcon, DollarSign, CheckCircle, Sparkles, X, Check } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ── Compact KPI card ────────────────────────────────────────────────────
const KpiCard = ({ label, value, sub, icon: Icon, tone = 'slate' }) => {
  const toneMap = {
    slate: 'text-slate-700 bg-slate-50 border-slate-200',
    red: 'text-red-700 bg-red-50 border-red-200',
    amber: 'text-amber-700 bg-amber-50 border-amber-200',
    green: 'text-emerald-700 bg-emerald-50 border-emerald-200',
    blue: 'text-blue-700 bg-blue-50 border-blue-200',
  };
  return (
    <div className={`rounded-xl border px-4 py-3 ${toneMap[tone]}`} data-testid={`kpi-${label.toLowerCase().replace(/\s+/g,'-')}`}>
      <div className="flex items-center gap-2 text-xs font-medium opacity-80">
        {Icon && <Icon className="w-3.5 h-3.5" />} {label}
      </div>
      <div className="text-2xl font-semibold mt-1">{value}</div>
      {sub && <div className="text-[11px] opacity-70 mt-0.5">{sub}</div>}
    </div>
  );
};

// ── Simple horizontal-bar chart (pure CSS, no chart lib needed) ────────
const HBar = ({ rows, colorClass = 'bg-indigo-500' }) => {
  const max = Math.max(1, ...rows.map(r => r.count));
  return (
    <div className="space-y-1.5">
      {rows.map((r, i) => (
        <div key={i} className="flex items-center gap-2 text-xs">
          <div className="w-32 shrink-0 text-slate-600 truncate">{r.stage || r.bucket}</div>
          <div className="flex-1 h-5 bg-slate-100 rounded overflow-hidden">
            <div className={`h-full ${colorClass}`} style={{ width: `${(r.count / max) * 100}%` }} />
          </div>
          <div className="w-8 text-right text-slate-700 font-medium tabular-nums">{r.count}</div>
        </div>
      ))}
    </div>
  );
};

const SUGGESTED_QUESTIONS = [
  '¿Por qué los usuarios abandonan la conversación?',
  '¿Qué patrones veo en los que preguntaron por precio y no compraron?',
  '¿En qué momento del flujo se pierde la mayoría?',
  '¿Cuáles son las preguntas que Camila no supo responder bien?',
  '¿Qué le cambiarías al prompt de Camila para aumentar conversión?',
];

export default function CamilaAnalyticsPage() {
  const navigate = useNavigate();
  const [kpis, setKpis] = useState(null);
  const [funnel, setFunnel] = useState([]);
  const [abandonment, setAbandonment] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notAdmin, setNotAdmin] = useState(false);

  const [messages, setMessages] = useState([]); // [{role,content, suggested_prompt?, current_prompt?, suggestion_reason?}]
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [rebuilding, setRebuilding] = useState(false);
  const [promptModal, setPromptModal] = useState(null); // { currentPrompt, newPrompt, reason, messageIdx }
  const [applyingPrompt, setApplyingPrompt] = useState(false);
  const [appliedIndices, setAppliedIndices] = useState(new Set()); // messageIdx marked as applied
  const chatEndRef = useRef(null);

  // ── Load aggregates ────────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [k, f, a] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/camila/kpis`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/admin/camila/funnel`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/admin/camila/abandonment`, { withCredentials: true }),
      ]);
      setKpis(k.data);
      setFunnel(f.data.funnel || []);
      setAbandonment(a.data.buckets || []);
    } catch (e) {
      if (e.response?.status === 403) setNotAdmin(true);
      else toast.error('Error cargando analytics de Camila');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, sending]);

  // ── Send a question to the AI analyst ─────────────────────────────────
  const sendQuestion = async (q) => {
    const question = (q ?? input).trim();
    if (!question || sending) return;
    setInput('');
    const nextMessages = [...messages, { role: 'user', content: question }];
    setMessages(nextMessages);
    setSending(true);
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/admin/camila/analyst-chat`,
        { question, history: messages.slice(-6).map(m => ({ role: m.role, content: m.content })) },
        { withCredentials: true },
      );
      setMessages([...nextMessages, {
        role: 'assistant',
        content: data.reply,
        suggested_prompt: data.suggested_prompt || null,
        current_prompt: data.current_prompt || null,
        suggestion_reason: data.suggestion_reason || null,
        suggestion_warning: data.suggestion_warning || null,
      }]);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'La analista no pudo responder');
      setMessages([...nextMessages, { role: 'assistant', content: '⚠️ No pude generar una respuesta. Intenta reformular.' }]);
    } finally { setSending(false); }
  };

  const applyPrompt = async () => {
    if (!promptModal) return;
    setApplyingPrompt(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/camila/apply-prompt`,
        { new_prompt: promptModal.newPrompt, reason: promptModal.reason || '' },
        { withCredentials: true },
      );
      toast.success('Prompt de Camila actualizado. El próximo mensaje de WhatsApp usará el nuevo prompt.');
      // Mark this assistant message as applied so the button turns into a checkmark.
      setAppliedIndices(prev => new Set([...prev, promptModal.messageIdx]));
      setPromptModal(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo aplicar el prompt');
    } finally { setApplyingPrompt(false); }
  };

  const triggerRebuild = async () => {
    setRebuilding(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/camila/rebuild?days=30&analyze_llm=true`,
        {},
        { withCredentials: true },
      );
      toast.success('Rebuild + análisis LLM completo');
      await fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Rebuild falló');
    } finally { setRebuilding(false); }
  };

  if (notAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center max-w-md">
          <AlertTriangle className="w-10 h-10 mx-auto text-amber-500 mb-3" />
          <h1 className="text-xl font-semibold text-slate-800">Acceso restringido</h1>
          <p className="text-sm text-slate-500 mt-2">Camila Analytics es solo para el rol admin.</p>
          <Button className="mt-4" onClick={() => navigate('/dashboard')}>Volver</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="camila-analytics-page">
      {/* ─── Modal: preview & confirm prompt change ────────────────── */}
      {promptModal && (
        <div className="fixed inset-0 z-[80] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="prompt-diff-modal">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b">
              <div>
                <h3 className="text-base font-semibold text-slate-800 flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-amber-600" /> Aplicar sugerencia al prompt de Camila
                </h3>
                {promptModal.reason && (
                  <p className="text-xs text-slate-500 mt-0.5">Razón: {promptModal.reason}</p>
                )}
              </div>
              <button
                onClick={() => setPromptModal(null)}
                className="text-slate-400 hover:text-slate-700"
                data-testid="prompt-diff-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {promptModal.warning && (
              <div className="px-5 py-2 bg-amber-50 border-b border-amber-200 text-xs text-amber-800" data-testid="prompt-diff-warning">
                {promptModal.warning}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-0 flex-1 overflow-hidden">
              <div className="flex flex-col border-r border-slate-200 overflow-hidden">
                <div className="px-4 py-2 bg-slate-100 border-b text-xs font-semibold text-slate-600 flex justify-between">
                  <span>ACTUAL</span>
                  <span className="text-slate-400 tabular-nums">{(promptModal.currentPrompt || '').length} chars</span>
                </div>
                <pre className="flex-1 overflow-y-auto p-4 text-xs text-slate-700 whitespace-pre-wrap font-mono bg-slate-50" data-testid="prompt-diff-current">
{promptModal.currentPrompt || '(vacío — Camila usa solo el prompt hardcoded)'}
                </pre>
              </div>
              <div className="flex flex-col overflow-hidden">
                <div className="px-4 py-2 bg-emerald-50 border-b border-emerald-200 text-xs font-semibold text-emerald-700 flex justify-between">
                  <span>SUGERIDO</span>
                  <span className="text-emerald-500 tabular-nums">{promptModal.newPrompt.length} chars</span>
                </div>
                <textarea
                  className="flex-1 p-4 text-xs text-slate-700 font-mono bg-emerald-50/30 resize-none focus:outline-none"
                  value={promptModal.newPrompt}
                  onChange={(e) => setPromptModal({ ...promptModal, newPrompt: e.target.value })}
                  data-testid="prompt-diff-suggested"
                />
              </div>
            </div>

            <div className="px-5 py-4 border-t bg-slate-50 flex items-center justify-between gap-3">
              <p className="text-[11px] text-slate-500">
                Se guardará el prompt anterior en <code className="bg-slate-200 px-1 rounded">camila_prompt_history</code> por si quieres volver atrás.
                El próximo mensaje que reciba Camila usará el nuevo prompt.
              </p>
              <div className="flex gap-2 shrink-0">
                <Button
                  variant="outline"
                  onClick={() => setPromptModal(null)}
                  data-testid="prompt-diff-cancel"
                >Cancelar</Button>
                <Button
                  onClick={applyPrompt}
                  disabled={applyingPrompt || !promptModal.newPrompt.trim()}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="prompt-diff-confirm"
                >
                  {applyingPrompt ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Aplicando…</> : <><Check className="w-4 h-4 mr-1.5" />Aplicar cambio</>}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <nav className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} data-testid="back-to-dashboard">
              <ArrowLeft className="w-4 h-4 mr-1" /> Dashboard
            </Button>
            <div>
              <h1 className="text-lg font-semibold text-slate-800">Camila Analytics</h1>
              <p className="text-[11px] text-slate-500">Solo admin · datos últimos 30 días</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={triggerRebuild} disabled={rebuilding} data-testid="rebuild-btn">
            {rebuilding ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
            Rebuild + análisis LLM
          </Button>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {loading ? (
          <div className="text-center py-20"><Loader2 className="w-6 h-6 mx-auto animate-spin text-slate-400" /></div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            {/* LEFT — aggregated data */}
            <div className="lg:col-span-2 space-y-4">
              {/* KPI grid */}
              <div className="grid grid-cols-2 gap-3">
                <KpiCard label="Total convos" value={kpis?.total_conversations || 0} icon={MessageSquare} tone="slate" />
                <KpiCard label="Abandono" value={`${Math.round((kpis?.abandonment_rate || 0) * 100)}%`} sub={`${kpis?.abandoned || 0} chats sin respuesta >24h`} icon={TrendingDown} tone="red" />
                <KpiCard label="Preguntan precio" value={kpis?.asked_about_price || 0} sub={`${kpis?.price_asked_no_convert || 0} no compraron`} icon={DollarSign} tone="amber" />
                <KpiCard label="Recibieron link" value={kpis?.link_sent || 0} sub="mensajes con analizandocantantes.com/suscribir" icon={LinkIcon} tone="blue" />
                <KpiCard label="Conversiones" value={kpis?.converted || 0} sub={`${Math.round((kpis?.conversion_rate || 0) * 100)}% del total`} icon={CheckCircle} tone="green" />
              </div>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Embudo</CardTitle>
                </CardHeader>
                <CardContent><HBar rows={funnel} colorClass="bg-indigo-500" /></CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">¿Dónde se pierden? (# mensajes antes de abandonar)</CardTitle>
                </CardHeader>
                <CardContent>
                  {abandonment.length > 0
                    ? <HBar rows={abandonment} colorClass="bg-red-400" />
                    : <p className="text-xs text-slate-400 italic">Sin data suficiente todavía.</p>}
                </CardContent>
              </Card>

              {kpis?.last_analyzed_at ? (
                <p className="text-[11px] text-slate-400 italic">Última corrida LLM: {new Date(kpis.last_analyzed_at).toLocaleString('es-CL')}</p>
              ) : (
                <p className="text-[11px] text-amber-600">⏳ Análisis LLM aún no completado. Haz clic en Rebuild o espera al cron nocturno.</p>
              )}
            </div>

            {/* RIGHT — conversational analyst */}
            <Card className="lg:col-span-3 flex flex-col h-[calc(100vh-11rem)]" data-testid="analyst-chat-card">
              <CardHeader className="pb-2 border-b">
                <CardTitle className="text-sm flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                  Habla con la analista IA — ella lee las conversaciones reales
                </CardTitle>
              </CardHeader>
              <CardContent className="flex-1 overflow-y-auto py-4 space-y-3" data-testid="analyst-chat-messages">
                {messages.length === 0 && (
                  <div className="space-y-3">
                    <p className="text-sm text-slate-500">Preguntale lo que quieras. Ejemplos:</p>
                    <div className="flex flex-wrap gap-2">
                      {SUGGESTED_QUESTIONS.map((q, i) => (
                        <button
                          key={i}
                          onClick={() => sendQuestion(q)}
                          className="text-xs px-3 py-1.5 rounded-full border border-slate-200 bg-white text-slate-600 hover:bg-slate-100 hover:border-slate-300 transition"
                          data-testid={`suggested-question-${i}`}
                        >
                          {q}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {messages.map((m, i) => (
                  <div key={i} className={`flex flex-col ${m.role === 'user' ? 'items-end' : 'items-start'}`}>
                    <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap ${
                      m.role === 'user'
                        ? 'bg-indigo-600 text-white rounded-br-md'
                        : 'bg-white border border-slate-200 text-slate-800 rounded-bl-md'
                    }`}>{m.content}</div>

                    {/* Actionable suggestion — appears under assistant messages
                        when the LLM emitted a PROMPT_SUGGESTION block. */}
                    {m.role === 'assistant' && m.suggested_prompt && (
                      <div className="mt-2 max-w-[85%] w-full" data-testid={`suggestion-block-${i}`}>
                        {appliedIndices.has(i) ? (
                          <div className="flex items-center gap-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
                            <Check className="w-3.5 h-3.5" /> Sugerencia aplicada al prompt de Camila
                          </div>
                        ) : (
                          <div className="flex items-center justify-between gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                            <div className="flex items-center gap-2 text-xs text-amber-800">
                              <Sparkles className="w-3.5 h-3.5" />
                              <span>{m.suggestion_reason || 'La analista sugiere un cambio al prompt de Camila.'}</span>
                            </div>
                            <Button
                              size="sm"
                              className="h-7 bg-amber-600 hover:bg-amber-700 text-white text-xs shrink-0"
                              onClick={() => setPromptModal({
                                currentPrompt: m.current_prompt || '',
                                newPrompt: m.suggested_prompt,
                                reason: m.suggestion_reason || '',
                                warning: m.suggestion_warning || null,
                                messageIdx: i,
                              })}
                              data-testid={`apply-suggestion-btn-${i}`}
                            >
                              Aplicar al prompt
                            </Button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
                {sending && (
                  <div className="flex justify-start">
                    <div className="bg-white border border-slate-200 rounded-2xl rounded-bl-md px-4 py-3 flex items-center gap-2 text-xs text-slate-500">
                      <Loader2 className="w-3.5 h-3.5 animate-spin" /> Analizando conversaciones...
                    </div>
                  </div>
                )}
                <div ref={chatEndRef} />
              </CardContent>
              <div className="border-t p-3 bg-slate-50 flex items-end gap-2">
                <Textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendQuestion(); } }}
                  placeholder="Pregúntale a la analista..."
                  className="min-h-[40px] max-h-32 resize-none text-sm bg-white"
                  data-testid="analyst-input"
                  disabled={sending}
                />
                <Button onClick={() => sendQuestion()} disabled={sending || !input.trim()} data-testid="analyst-send">
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </Button>
              </div>
            </Card>
          </div>
        )}
      </main>
    </div>
  );
}
